/* eslint-disable unused-imports/no-unused-imports */
import React, {FC, LegacyRef, useCallback, useEffect, useRef} from "react"

import {breakpoints} from "@next/themes"
import {MobileThumbnailConfig} from "models/ProductSummary"
import PsImageContainer from "../../../imageContainer"
import {ColourwayImageSlide} from "../ColourwayImageSlide"
import {EmblaContainer, EmblaSlide, EmblaViewport} from "../../components"
import {ColourwaySwiperDetails} from "../ColourwaySwiperDetails"
import {SlideDetail} from "../../helpers/useSwiperColourway"
import {TextModel} from "../../../../models/Text"
import getActiveBreakpoint from "../../../../utils/getActiveBreakpoint"
import {WHEN_TO_LOAD_ADDITIONAL_COLOURCHIPS} from "../../../../config/constants"
import ColourwaySingleSlide from "../ColourwaySingleSlide"
import useMediaQuery from "../../../../hooks/useMediaQuery"

interface SwiperCarouselProps {
    slides: SlideDetail[]
    currentSlideIndex: number
    text: TextModel
    mobileThumbnailConfig: MobileThumbnailConfig
    fetchAdditionalColourchipsThunk: () => void
    isOnServer?: boolean
    emblaRef: LegacyRef<HTMLDivElement> | undefined
    textAlignment: string
    colourchipSelected: number
    lazyloadProductImages: boolean
}

export const SwiperCarousel: FC<SwiperCarouselProps> = ({
    slides,
    text,
    mobileThumbnailConfig,
    fetchAdditionalColourchipsThunk,
    isOnServer = false,
    emblaRef,
    textAlignment,
    currentSlideIndex,
    ...colourwaysImageDetailsProps
}) => {
    const firstItemNumber = slides[0].id
    const breakpointsConfig = [
        {
            size: "xs",
            isActive: true, // screen size is always > 0
            slidesPerView: mobileThumbnailConfig.xs,
        },
        {
            size: "sm",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.sm}px)`),
            slidesPerView: mobileThumbnailConfig.sm,
        },
        {
            size: "md",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.md}px)`),
            slidesPerView: mobileThumbnailConfig.md,
        },
        {
            size: "lg",
            isActive: useMediaQuery(`(min-width:${breakpoints.values.lg}px)`),
            // large & xl don't use `slidesPerView`
        },
    ]
    const activeBreakpoint = getActiveBreakpoint(breakpointsConfig)
    const isDesktopBreakPoint = activeBreakpoint.size === "lg"

    const additionalColourChipsLoaded = useRef(false)
    useEffect(() => {
        // window.requestIdleCallback(() => {
        if (
            !isDesktopBreakPoint &&
            currentSlideIndex === WHEN_TO_LOAD_ADDITIONAL_COLOURCHIPS &&
            !additionalColourChipsLoaded.current
        ) {
            additionalColourChipsLoaded.current = true
            fetchAdditionalColourchipsThunk()
        }
        // })
    }, [currentSlideIndex, fetchAdditionalColourchipsThunk, isDesktopBreakPoint])

    const slideAttributes = useCallback(
        (isVisible: boolean, index: number) =>
            isVisible
                ? {
                      className: "carousel-slide",
                      "aria-current": (currentSlideIndex === index || "false") as true | "false",
                      "data-index": index,
                  }
                : {},
        [currentSlideIndex],
    )

    if (slides.length === 1) {
        const colourwaysImageDetails = {
            fits: slides[0].fits,
            isOnSale: slides[0].isColourwayOnSale,
            showFitsIcons: slides[0].showFitsIcons,
            lazyloadProductImages: colourwaysImageDetailsProps.lazyloadProductImages,
            text,
            displayNewIn: slides[0].displayNewIn,
            colourchipSelected: colourwaysImageDetailsProps.colourchipSelected,
        }

        return <ColourwaySingleSlide slide={slides[0]} isOnServer={isOnServer} imageDetails={colourwaysImageDetails} />
    }

    return (
        <PsImageContainer>
            <EmblaViewport className="swiper-container" ref={emblaRef} dir={textAlignment}>
                <EmblaContainer className="swiper-wrapper">
                    {slides.map((slideProps, index) => {
                        const isVisible =
                            index === currentSlideIndex ||
                            index === currentSlideIndex - 1 ||
                            index === currentSlideIndex + 1

                        return (
                            <EmblaSlide
                                {...slideAttributes(isVisible, index)}
                                $isVisible={isVisible}
                                key={slideProps.id}
                            >
                                {isVisible ? (
                                    <ColourwayImageSlide
                                        isActiveSlide={currentSlideIndex === index}
                                        key={slideProps.id}
                                        firstItemNumber={firstItemNumber}
                                        {...slideProps}
                                    >
                                        <ColourwaySwiperDetails
                                            isOnServer={isOnServer}
                                            {...colourwaysImageDetailsProps}
                                            displayNewIn={slideProps.displayNewIn}
                                            text={text}
                                            fits={slideProps.fits}
                                            isOnSale={slideProps.isColourwayOnSale}
                                            showFitsIcons={slideProps.showFitsIcons}
                                        />
                                    </ColourwayImageSlide>
                                ) : null}
                            </EmblaSlide>
                        )
                    })}
                </EmblaContainer>
            </EmblaViewport>
        </PsImageContainer>
    )
}
